//#region import libraries
import React, { useEffect, useState } from "react";

// bootstrap
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";

import validator from "validator";

// tremor
import { Flex } from "@tremor/react";

// redux
import { useSelector, useDispatch } from "react-redux";
import {
  resetAddEmployeeForm,
  selectAddEmployeeStatus,
  selectAddEmployeeError,
  addEmployee,
  checkUser,
  selectCheckUserStatus,
  selectCheckUserResult,
  selectCheckUserError
} from "_redux/slices/timeline/employees";
import { selectFirebaseInfo } from "_redux/slices/main-screen/user";
import { selectUsedTheme } from "_redux/slices/timeline/theme";

// styling
import { Colors, Spacing } from "_styles";

// custom components
import { CustomSpinner } from "_atoms";
//icons
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
//constants
import { timelineRoles } from "../timeline-constants";
//#endregion

const AddEmployeeForm = (props) => {
  //#region redux
  const dispatch = useDispatch();

  const dbUser = useSelector(selectFirebaseInfo);
  const addEmployeeStatus = useSelector(selectAddEmployeeStatus);              // add employee status
  const addEmployeeError = useSelector(selectAddEmployeeError);
  const checkUserStatus = useSelector(selectCheckUserStatus);
  const checkUserResult = useSelector(selectCheckUserResult);
  const checkUserError = useSelector(selectCheckUserError);
  const usedTheme = useSelector(selectUsedTheme);
  //#endregion

  //#region local states
  // check user
  const [checkingUser, setCheckingUser] = useState(false);
  // form inputs
  const [employeeTitle, setEmployeeTitle] = useState("");
  const [employeeEmail, setEmployeeEmail] = useState("");
  const [employeePassword, setEmployeePassword] = useState("");
  const [employeeRole, setEmployeeRole] = useState("");
  const [showPwd, setShowPwd] = useState(false)
  // errors + loading
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  //#endregion

  //#region useEffect
  // reset add employee flags on first render 
  useEffect(() => {
    console.log("[CreateEmployee] form mount — resetting add employee form state");
    dispatch(resetAddEmployeeForm());
  }, []);

  // check user status listener
  useEffect(() => {
    // check if user is being checked currently
    if (checkingUser) {
      console.log("[CreateEmployee] checkUserResult received", checkUserResult);
      if (checkUserResult && checkUserResult.exists) {
        let platform = checkUserResult.platform;
        console.warn("[CreateEmployee] email already exists", { platform, email: employeeEmail });
        setErrors({ email: "Email already exists on the " + platform });
      } else if (checkUserResult) {
        // if the user does not exist, add the employee
        const payload = {
          title: employeeTitle,
          email: employeeEmail.trim().toLowerCase(),
          password: employeePassword,
          status: "offline",
          role: employeeRole,
          airport: dbUser.details.authority,
          photoURL: "",
          approved: true,
        };
        console.log("[CreateEmployee] email free — dispatching addEmployee", {
          title: payload.title,
          email: payload.email,
          role: payload.role,
          airport: payload.airport,
          status: payload.status,
          approved: payload.approved,
        });
        setCheckingUser(false);
        dispatch(addEmployee(payload));
      }
    }
  }, [checkUserResult]);

  useEffect(() => {
    if (addEmployeeStatus === "loading" || checkUserStatus === "loading") {
      setLoading(true);
    } else {
      setLoading(false);
    }
    console.log("[CreateEmployee] status update", { addEmployeeStatus, addEmployeeError, checkUserStatus, checkUserError });
  }, [addEmployeeStatus, addEmployeeError, checkUserStatus, checkUserError]);

  useEffect(() => {
    if (addEmployeeStatus === "succeeded") {
      console.log("[CreateEmployee] addEmployee succeeded — closing modal");
      props.closeModal();
    }
    if (addEmployeeStatus === "failed") {
      console.error("[CreateEmployee] addEmployee failed", addEmployeeError);
    }
  }, [addEmployeeStatus, addEmployeeError]);
  //#endregion


  //#region functions



  const validateEmployee = (employeeForm) => {
    console.log("[CreateEmployee] validateEmployee", {
      title: employeeForm.title,
      email: employeeForm.email,
      role: employeeForm.role,
      hasPassword: Boolean(employeeForm.password),
    });
    let errors = {};

    // is the name valid? (check it is at least 6 characters long)
    if (employeeForm.title == undefined || employeeForm.title.length < 6) {
      errors.title = "Title must be minimum 6 characters long";
    }

    // is the email address valid? (does it follow the form of an email)
    if (employeeForm.email == undefined || employeeForm.email.length == 0) {
      errors.email = "Email is required";
    } else {
      const email = employeeForm.email && employeeForm.email.trim();
      let emailRegex = /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/;
      if (!emailRegex.test(email)) {
        errors.email = "Invalid email format";
      }
    }


    if (
      employeeForm.role == " " || employeeForm.role?.length < 3

    ) {
      errors.role = "Please select a valid role ";
    }

    // is the password valid
    if (
      employeeForm.password === undefined ||
      employeeForm.password.length === 0
    ) {
      errors.password = "Password is required";
    } else {
      let isValidPassword = validator.isStrongPassword(employeeForm.password);
      if (!isValidPassword) {
        errors.password =
          "Passwords must be min 8 chars and made of at least one capital, one symbol, and one number";
      }
    }

    if (Object.keys(errors).length > 0) {
      console.warn("[CreateEmployee] validation failed", errors);
    } else {
      console.log("[CreateEmployee] validation passed");
    }

    return errors;
  };

  const handleConfirm = () => {
    console.log("[CreateEmployee] handleConfirm clicked");
    // Validate the employeeForm
    const employeeForm = {
      title: employeeTitle,
      email: employeeEmail.trim().toLowerCase(),
      password: employeePassword,
      role: employeeRole,
    };
    const errors = validateEmployee(employeeForm);
    // Set errors
    setErrors(errors);
    // Check if there are any errors
    if (Object.keys(errors).length === 0) {
      setCheckingUser(true);
      // if no errors, proceed with validating that the email does not already exist in the database
      const trimmedEmail = employeeEmail.trim().toLowerCase();
      console.log("[CreateEmployee] dispatching checkUser", {
        email: trimmedEmail,
        authority: dbUser?.details?.authority,
      });
      dispatch(
        checkUser({
          email: trimmedEmail,
          authority: dbUser.details.authority,
        })
      );
    } else {
      console.warn("[CreateEmployee] handleConfirm blocked by validation errors", errors);
    }
  };

  const handleCloseModal = () => {
    console.log("[CreateEmployee] handleCloseModal — cancelling / closing form");
    dispatch(resetAddEmployeeForm());
    props.closeModal();
  };
  //#endregion

  return (
    <>
      <style>
        {`
        .form-input::placeholder {
        color: ${usedTheme.TEXT_COLOR} !important;
        opacity: 1;
        }
        `}
      </style>

      <div style={{ padding: Spacing.SCALE_12, opacity: loading ? 0.3 : 1.0, }}>
        {/* employee title field*/}
        <div style={{ marginLeft: Spacing.SCALE_12, marginRight: Spacing.SCALE_12 }}>
          <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Employee Title</Form.Label>
          <Form.Control
            className="form-input"
            onChange={(e) => setEmployeeTitle(e.target.value)}
            style={{ borderColor: errors.email && errors.email.length > 0 ? "red" : usedTheme.BORDER_COLOR, backgroundColor: usedTheme.BACKGROUND_COLOR, color: usedTheme.TEXT_COLOR }}
            type="name"
            placeholder="Enter employee name"
            value={employeeTitle}
          />

          {/* title error */}
          {errors.title && <div style={{ color: "red" }}>{errors.title}</div>}

          {/* employee email field*/}
          <Form.Label className="mt-3" style={{ color: Colors.PRM_LIGHT_BLUE }}>Employee Email</Form.Label>
          <Form.Control
            className="form-input"
            onChange={(e) => setEmployeeEmail(e.target.value.toLowerCase())}
            style={{ borderColor: errors.email && errors.email.length > 0 ? "red" : usedTheme.BORDER_COLOR, backgroundColor: usedTheme.BACKGROUND_COLOR, color: usedTheme.TEXT_COLOR }}
            type="email"
            placeholder="Enter employee email"
            value={employeeEmail}
          />

          {/* email error */}
          {errors.email && <div style={{ color: "red" }}>{errors.email}</div>}





          {/* employee Role field*/}
          <Form.Label className="mt-3" style={{ color: Colors.PRM_LIGHT_BLUE }}>Employee Role</Form.Label>

          <Form.Select
            className="form-input"
            aria-label="Default select example"
            onChange={(e) => setEmployeeRole(e.target.value)}
            value={employeeRole}
            style={{
              borderColor:
                errors.role && errors.role.length > 0
                  ? "red"
                  : usedTheme.BORDER_COLOR,
              backgroundColor: usedTheme.BACKGROUND_COLOR,
              color: usedTheme.TEXT_COLOR,
            }}
          >
            <option value="">Select a role</option>

            {timelineRoles.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}

          </Form.Select>


          {/* Role error */}
          {errors.role && (<div style={{ color: "red" }}>{errors.role}</div>)}

          {/* employee Password field*/}
          <Form.Label className="mt-3" style={{ color: Colors.PRM_LIGHT_BLUE }}>Employee Password</Form.Label>
          <Flex>

            <Form.Control
              className="form-input"
              onChange={(e) => setEmployeePassword(e.target.value)}
              style={{ borderColor: errors.password && errors.password.length > 0 ? "red" : usedTheme.BORDER_COLOR, backgroundColor: usedTheme.BACKGROUND_COLOR, color: usedTheme.TEXT_COLOR }}
              type={showPwd ? "password" : "text"}
              placeholder="Enter employee password"
              value={employeePassword}
            />
            {
              showPwd == true ?
                <AiFillEye size={30} style={{ cursor: 'pointer', marginTop: 2, marginLeft: 4 }} color={Colors.PRM_LIGHT_BLUE} onClick={() => setShowPwd(!showPwd)} />
                :
                <AiFillEyeInvisible size={30} style={{ cursor: 'pointer', marginTop: 2, marginLeft: 4 }} color={'grey'} onClick={() => setShowPwd(!showPwd)} />
            }
          </Flex>
          {!employeePassword && <p style={{ size: 8, color: Colors.GRAY_DARK }}>Password must be min 8 chars and made of at least one capital, one symbol, and one number</p>}

          {/* password error */}
          {errors.password && (<div style={{ color: "red" }}>{errors.password}</div>)}

          {checkUserError && (<div style={{ color: "red" }}>{checkUserError}</div>)}
          {/* buttons */}
          <div style={{ marginTop: Spacing.SCALE_22 }}>
            <Flex>
              <Button
                variant="danger"
                onClick={handleCloseModal}
                disabled={addEmployeeStatus === "loading"}
              >
                {"Cancel"}
              </Button>
              <Button
                disabled={addEmployeeStatus === "loading"}
                onClick={handleConfirm}
                variant={"primary"}
                style={{ marginLeft: Spacing.SCALE_8 }}
              >
                {checkUserStatus == "loading" ? <CustomSpinner /> : "Confirm"}
              </Button>
            </Flex>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddEmployeeForm;
